import { FC, memo, useMemo } from 'react'
import { ColumnDef, TableOptions, getCoreRowModel } from '@tanstack/react-table'
import BaselineError from '~icons/ic/baseline-error'
import {
  VerifyBulkInsertTasks_Row,
  VerifyBulkInsertTasks_Row_Col,
} from '@crew/apis/task/models/verifyBulkInsertTasks/response'
import {
  VerifyBulkInsertTasksErrorCode,
  VerifyBulkInsertTasksMetadataField,
} from '@crew/enums/domain'
import { useTranslation } from '@crew/modules/i18n'
import { uniqueString } from '@crew/utils'
import { getEnumDataByValue } from '@crew/utils/enum'
import { TaskPriorities } from 'enums/app'
import { CrewPopover } from 'components/devextreme/crewPopover'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import { CrewTaskPriority } from 'components/elements/crewTaskPriority/crewTaskPriority'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewBadge } from 'components/elements/crewBadge/crewBadge'

// Popoverの最大幅
const MAX_POPOVER_WIDTH = 300

type ItemStatusProps = {
  item: VerifyBulkInsertTasks_Row_Col
  columnKey?: string
}

const ItemStatus = memo((props: ItemStatusProps) => {
  const { t } = useTranslation()
  // ユニークキー生成
  const popoverKey = useMemo(() => uniqueString(), [])

  return (
    <div className="flex flex-row items-center gap-x-2.5">
      {props.item.hasError && (
        <div id={`error-${popoverKey}`}>
          <BaselineError width={20} height={20} className="crew-error-text" />
          <CrewPopover
            target={`#error-${popoverKey}`}
            showEvent="mouseenter"
            hideEvent="mouseleave"
            position="top"
            maxWidth={MAX_POPOVER_WIDTH}
          >
            <span className="text-sm">
              {
                // 必須項目エラー
                props.item.errorCode ===
                VerifyBulkInsertTasksErrorCode.RequiredItem
                  ? t(`message.task.confirmTaskImportError.required`)
                  : // 存在しないエラー
                  props.item.errorCode ===
                    VerifyBulkInsertTasksErrorCode.Notfound
                  ? t(`message.task.confirmTaskImportError.notFound`)
                  : // 無効な値エラー
                  props.item.errorCode ===
                    VerifyBulkInsertTasksErrorCode.InvalidValue
                  ? (() => {
                      // カラムごとの無効な値エラーメッセージを表示
                      switch (props.columnKey) {
                        case 'startDate':
                        case 'dueDate':
                        case 'estimatedWorkTimes':
                          return t(
                            `message.task.confirmTaskImportError.invalidFormat.${props.columnKey}`
                          )
                        default:
                          return t(
                            `message.task.confirmTaskImportError.invalidValue`
                          )
                      }
                    })()
                  : t(
                      `message.task.confirmTaskImportError.${props.item.errorCode}`
                    )
              }
            </span>
          </CrewPopover>
        </div>
      )}
    </div>
  )
})

export type ConfirmTaskImportTableProps = {
  importTasks: VerifyBulkInsertTasks_Row[]
}

export const ConfirmTaskImportTable: FC<ConfirmTaskImportTableProps> = memo(
  (props) => {
    const { t } = useTranslation()

    const columns = useMemo<ColumnDef<VerifyBulkInsertTasks_Row>[]>(
      () => [
        {
          id: 'subject',
          header: () => t('label.confirmTaskImportTable.subject'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5 truncate">
              {row.original.subject.value}
              <ItemStatus item={row.original.subject} />
            </div>
          ),
          size: 200,
          minSize: 50,
        },
        {
          id: 'taskKind',
          header: () => t('label.confirmTaskImportTable.kind'),
          cell: ({ row }) => {
            if (!row.original.taskKind.internalValue) {
              return <ItemStatus item={row.original.taskKind} />
            }

            return (
              <div className="w-full flex items-center gap-x-2.5 truncate">
                <CrewBadge
                  displayColor={
                    row.original.taskKind.metadata?.[
                      VerifyBulkInsertTasksMetadataField.DisplayColor
                    ] || ''
                  }
                >
                  {row.original.taskKind.value}
                </CrewBadge>
                <ItemStatus item={row.original.taskKind} />
              </div>
            )
          },
          size: 100,
          minSize: 50,
        },
        {
          id: 'taskPriority',
          header: () => t('label.confirmTaskImportTable.priority'),
          cell: ({ row }) => {
            if (!row.original.taskPriority.internalValue) {
              return (
                <div className="w-full flex items-center gap-x-2.5 truncate">
                  {row.original.taskPriority?.value}
                  <ItemStatus item={row.original.taskPriority} />
                </div>
              )
            }
            const taskPriorityNum = parseInt(
              row.original.taskPriority.internalValue
            )
            const taskPriority = getEnumDataByValue(
              TaskPriorities,
              taskPriorityNum
            )

            return (
              <div className="w-full flex items-center gap-x-0.5 truncate">
                {taskPriority && (
                  <CrewTaskPriority
                    taskPriority={taskPriority.value}
                    className="justify-center"
                  />
                )}
                <ItemStatus item={row.original.taskPriority} />
              </div>
            )
          },
          size: 80,
          minSize: 50,
        },
        {
          id: 'taskState',
          header: () => t('label.confirmTaskImportTable.state'),
          cell: ({ row }) => {
            if (!row.original.taskState.internalValue) {
              return (
                <div className="w-full flex items-center gap-x-2.5 truncate">
                  {row.original.taskState?.value}
                  <ItemStatus item={row.original.taskState} />
                </div>
              )
            }

            return (
              <div className="w-full flex items-center gap-x-2.5 truncate">
                <CrewBadge
                  displayColor={
                    row.original.taskState.metadata?.[
                      VerifyBulkInsertTasksMetadataField.DisplayColor
                    ] || ''
                  }
                >
                  {row.original.taskState.value}
                </CrewBadge>
                <ItemStatus item={row.original.taskState} />
              </div>
            )
          },
          size: 100,
          minSize: 50,
        },
        {
          id: 'assignToUser',
          header: () => t('label.confirmTaskImportTable.assignToUser'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5 truncate">
              {row.original.assignToUser &&
                row.original.assignToUser.internalValue !== null &&
                row.original.assignToUser.value !== null && (
                  <CrewUserAvatar
                    size={CrewAvatarSize.xs}
                    userId={row.original.assignToUser.internalValue}
                    displayName={row.original.assignToUser.value}
                    cacheValue={row.original.assignToUser.value} // version情報がないため、valueのみでキャッシュ
                    showLabel={true}
                    disableClick={true}
                  />
                )}
              {row.original.assignToUser && (
                <ItemStatus
                  item={row.original.assignToUser}
                  columnKey="assignToUser"
                />
              )}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        {
          id: 'startDate',
          header: () => t('label.confirmTaskImportTable.startDate'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5 truncate">
              {row.original.startDate?.value}
              {row.original.startDate && (
                <ItemStatus
                  item={row.original.startDate}
                  columnKey="startDate"
                />
              )}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        {
          id: 'dueDate',
          header: () => t('label.confirmTaskImportTable.dueDate'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5 truncate">
              {row.original.dueDate?.value}
              {row.original.dueDate && (
                <ItemStatus item={row.original.dueDate} columnKey="dueDate" />
              )}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        {
          id: 'estimatedWorkTimes',
          header: () => t('label.confirmTaskImportTable.estimatedWorkTimes'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5 truncate">
              {row.original.estimatedWorkTimes?.value}
              {row.original.estimatedWorkTimes && (
                <ItemStatus
                  item={row.original.estimatedWorkTimes}
                  columnKey="estimatedWorkTimes"
                />
              )}
            </div>
          ),
          size: 80,
          minSize: 50,
        },
        {
          id: 'taskCategory',
          header: () => t('label.confirmTaskImportTable.category'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5 truncate">
              {row.original.taskCategory?.value}
              {row.original.taskCategory && (
                <ItemStatus
                  item={row.original.taskCategory}
                  columnKey="taskCategory"
                />
              )}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
      ],
      [t]
    )

    const tableOptions: TableOptions<VerifyBulkInsertTasks_Row> = {
      data: props.importTasks,
      columns,
      columnResizeMode: 'onChange',
      getCoreRowModel: getCoreRowModel(),
      meta: {
        headerRowHeight: 35,
        dataRowHeight: 40,
      },
    }

    return (
      <div className="max-h-96 flex">
        <div className="grow overflow-hidden">
          <CrewTable
            tableOptions={tableOptions}
            showPager={false}
            showColumnSelector={false}
          />
        </div>
      </div>
    )
  }
)
